import AutorenewIcon from "@mui/icons-material/AutorenewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import biApi from "../../../../../api/biApi";
import type { WorkbookRun, WorkbookRunStatus } from "../../../../../api/types/workbookRuns.types";
import { isDownloadableRun, isFinishedStatus } from "../workbookRunUtils";

interface Props {
  workbookRun: WorkbookRun;
  setWorkbookRun: (workbookRun: WorkbookRun) => void;
}

const downloadRun = withErrorHandling(biApi.getWorkbookRunDownloadInfo);
const cancelRun = withErrorHandling(biApi.cancelWorkbookRuns);
const rerunRun = withErrorHandling(biApi.rerunWorkbookRun);

const rerunableStatuses = ["Canceled", "Error", "Completed"] as WorkbookRunStatus[];
const deletedTemplateTooltip = "This workbook cannot be rerun because its template was deleted.";

const WorkbookRunDetailsActions = ({ workbookRun, setWorkbookRun }: Props) => {
  const [downloading, setDownloading] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const { sendNotificationError } = useNotificationContext();
  const navigate = useNavigate();

  const handleRerun = async () => {
    setRerunning(true);
    const [result, error] = await rerunRun(workbookRun.id);
    if (error) {
      logError(error, "[WorkbookRunDetailsActions] handleRerun");
      sendNotificationError(error.message ?? "Could not rerun the run");
    } else {
      navigate(`../run/${result?.runId}`);
    }
    setRerunning(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    const [downloadInfo, error] = await downloadRun(workbookRun.id);
    if (error) {
      logError(error, "[WorkbookRunDetailsActions] handleDownload");
      sendNotificationError(error.message ?? "Could not download the run");
    } else {
      await downloadFileFromUrl(downloadInfo.downloadUrl);
    }
    setDownloading(false);
  };

  const handleCancel = async () => {
    setCanceling(true);
    const [result, error] = await cancelRun({ runs: { [workbookRun.id]: workbookRun.operationId ?? "" } });
    const canceledRun = result?.results[workbookRun.id]?.workbookRun;

    if (error || !canceledRun) {
      logError(error, "[WorkbookRunDetailsActions] handleCancel");
      sendNotificationError(error?.message ?? "Could not cancel the run");
      setCanceling(false);
      return;
    }
    setWorkbookRun({ ...workbookRun, ...canceledRun });
    if (!result?.results[workbookRun.id]?.isCanceled) {
      sendNotificationError("Failed to cancel the run");
    }

    setCanceling(false);
  };

  const isDownloadable = isDownloadableRun(workbookRun);
  const isRerunnable = rerunableStatuses.includes(workbookRun.status);

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {!isFinishedStatus(workbookRun.status) && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloseRoundedIcon />}
          onClick={handleCancel}
          loading={canceling}
        >
          Cancel
        </Button>
      )}
      <AuthorizedBox permissions={["ManageReports"]}>
        {isRerunnable && (
          <TooltipForDisabledAction
            title={workbookRun.isTemplateDeleted ? deletedTemplateTooltip : undefined}
            isActionDisabled={workbookRun.isTemplateDeleted}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={handleRerun}
              loading={rerunning}
              disabled={workbookRun.isTemplateDeleted}
            >
              Rerun
            </Button>
          </TooltipForDisabledAction>
        )}
      </AuthorizedBox>
      <Button
        disabled={!isDownloadable}
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        loading={downloading}
      >
        Download
      </Button>
    </Box>
  );
};

export default WorkbookRunDetailsActions;
